/**
 * 사용자 추가·수정 폼 모달 (Phase 4-1).
 *
 * 모드:
 *   - 'create' : 로그인 계정 정보 + 직원 정보 동시 입력 → 두 행 함께 생성.
 *                직원 ID = 사용자 ID 동일하게 사용 (사용자 결정, 별도 입력 X).
 *   - 'edit'   : initial 채움. userId/empId readonly. 비번은 별도 모달에서 재설정.
 *                직원 정보 수정은 연결된 emp_id 가 있을 때만.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import { useDirtyClose } from '../lib/useDirtyClose';
import { DraggableModal } from './DraggableModal';
import type {
  AdminUserCreateInput,
  AdminUserRow,
  AdminUserUpdateInput,
  UserRole,
} from '../lib/types';

interface CreateProps {
  mode: 'create';
  initial?: never;
  isSelf?: never;
  onClose: () => void;
  onSaved: (userId: string) => void;
}
interface EditProps {
  mode: 'edit';
  initial: AdminUserRow;
  /** 본인 계정 수정 시 — role/active 변경 차단 표시 */
  isSelf: boolean;
  onClose: () => void;
  onSaved: (userId: string) => void;
}
type Props = CreateProps | EditProps;

const ROLE_INFO: Record<UserRole, { label: string; sub: string }> = {
  admin: { label: 'admin', sub: '(전체)' },
  mgmt: { label: 'mgmt', sub: '(관리자급)' },
  sales: { label: 'sales', sub: '(영업부)' },
  viewer: { label: 'viewer', sub: '(일반직원)' },
};

export function UserFormModal(props: Props) {
  const qc = useQueryClient();
  const isEdit = props.mode === 'edit';
  const initial = isEdit ? props.initial : null;
  const isSelf = isEdit && props.isSelf;

  // ── create 전용 ──
  // 사용자 ID = 직원 ID 동일하게 사용 (사용자 결정, 별도 직원 ID 칸 없음)
  const [userIdInput, setUserIdInput] = useState('');
  const [password, setPassword] = useState('');

  // ── 공통 (create + edit) ──
  const [name, setName] = useState<string>(initial?.empName ?? '');
  const [title, setTitle] = useState<string>(initial?.empTitle ?? '');
  const [department, setDepartment] = useState<string>(
    initial?.empDepartment ?? '',
  );
  // 팀 — 부서 아래 조직(예: 영업팀·영업지원팀·기술지원팀). 팀장이 없어 결재선은 안 본다.
  const [team, setTeam] = useState<string>(initial?.empTeam ?? '');
  // 휴대폰 (v2.211.0) — 메일 꼬리말 회사 정보의 M · 견적서·발주서 담당자 연락처. 관리자만 여기서 고친다.
  const [mobile, setMobile] = useState<string>(initial?.empMobile ?? '');
  const [role, setRole] = useState<UserRole>(initial?.role ?? 'viewer');
  const [email, setEmail] = useState<string>(initial?.email ?? '');
  const [isActive, setIsActive] = useState<boolean>(
    initial?.isActive ?? true,
  );

  const { markDirty, requestClose } = useDirtyClose(props.onClose);

  const createMut = useMutation({
    mutationFn: (input: AdminUserCreateInput) => adminApi.createUser(input),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      props.onSaved(res.userId);
    },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdminUserUpdateInput }) =>
      adminApi.updateUser(id, input),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      props.onSaved(res.userId);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isEdit && initial) {
      const input: AdminUserUpdateInput = {};
      if (!isSelf) {
        if (role !== initial.role) input.role = role;
        if (isActive !== initial.isActive) input.isActive = isActive;
      }
      const trimmedEmail = email.trim() || null;
      if (trimmedEmail !== (initial.email ?? null)) {
        input.email = trimmedEmail;
      }
      // 직원 정보 — emp_id 미연결 시 백엔드가 자동 생성 (emp_id = user_id)
      const trimmedName = name.trim();
      const trimmedTitle = title.trim() || null;
      const trimmedDept = department.trim() || null;
      const trimmedTeam = team.trim() || null;
      const trimmedMobile = mobile.trim() || null;
      if (initial.empId) {
        // 이미 연결된 직원 정보 갱신
        if (trimmedName !== (initial.empName ?? '')) input.empName = trimmedName;
        if (trimmedTitle !== (initial.empTitle ?? null)) input.empTitle = trimmedTitle;
        if (trimmedDept !== (initial.empDepartment ?? null))
          input.empDepartment = trimmedDept;
        if (trimmedTeam !== (initial.empTeam ?? null)) input.empTeam = trimmedTeam;
        if (trimmedMobile !== (initial.empMobile ?? null)) input.empMobile = trimmedMobile;
      } else if (trimmedName) {
        // 미연결 + 이름 입력됨 → 백엔드가 employee 자동 생성 + emp_id 연결
        input.empName = trimmedName;
        input.empTitle = trimmedTitle;
        input.empDepartment = trimmedDept;
        input.empTeam = trimmedTeam;
        input.empMobile = trimmedMobile;
      }
      if (Object.keys(input).length === 0) {
        // 변경 없음 — 단순 닫기
        props.onClose();
        return;
      }
      updateMut.mutate({ id: initial.userId, input });
      return;
    }

    // create — 로그인 계정 + 직원 정보 동시 생성
    // empId 는 userId 와 동일하게 사용 (사용자 결정, 별도 입력 X)
    const trimmedUserId = userIdInput.trim();
    if (!trimmedUserId || !name.trim()) return;
    createMut.mutate({
      userId: trimmedUserId,
      password,
      role,
      email: email.trim() || null,
      isActive,
      empId: trimmedUserId,
      name: name.trim(),
      title: title.trim() || null,
      department: department.trim() || null,
      team: team.trim() || null,
      mobile: mobile.trim() || null,
    });
  }

  const saving = createMut.isPending || updateMut.isPending;
  const error = createMut.error ?? updateMut.error;

  return (
    <DraggableModal onClose={requestClose} initialWidth={600} closeOnBackdrop={false} closeOnEsc={false}>
      <div onInput={markDirty} onChange={markDirty}>
        <div className="dm-drag-handle cursor-move flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
          <h2 className="ui-modal-title">
            {isEdit ? '계정 수정' : '계정 추가'}
            {isEdit && initial && (
              <span className="ml-2 text-xs text-slate-500 font-mono">
                {initial.userId}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* create: ID/비번 */}
          {!isEdit && (
            <fieldset>
              <legend className="text-sm font-semibold mb-2">로그인 정보</legend>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="ui-label">사용자 ID *</label>
                  <input
                    value={userIdInput}
                    onChange={(e) => setUserIdInput(e.target.value)}
                    required
                    placeholder="예: hwang"
                    className="ui-input w-full mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="ui-label">비밀번호 * (8자 이상)</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="ui-input w-full mt-1"
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* 직원 정보 — 항상 표시.
              create: emp_id = user_id 자동.
              edit + emp_id 미연결: 입력 후 저장 시 백엔드가 emp_id = user_id 로 자동 생성·연결. */}
          <fieldset>
            <legend className="text-sm font-semibold mb-2">
              직원 정보
              {isEdit && !initial?.empId && (
                <span className="ml-2 text-xs text-amber-700 font-normal">
                  (직원 정보 미연결 — 입력하면 자동 등록됨)
                </span>
              )}
            </legend>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2">
                <label className="ui-label">이름 *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="ui-input w-full mt-1"
                />
              </div>
              <div>
                <label className="ui-label">직급</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="예: 부장 / 차장"
                  className="ui-input w-full mt-1"
                />
              </div>
              <div>
                <label className="ui-label">부서</label>
                <input
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="예: 시스템사업부"
                  className="ui-input w-full mt-1"
                />
              </div>
              <div className="col-span-2">
                <label className="ui-label">팀</label>
                <input
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  placeholder="예: 영업팀 / 영업지원팀 / 기술지원팀 (없으면 비워 둠)"
                  className="ui-input w-full mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  부서 아래 조직입니다. 결재선은 <b>부서</b>로 정해지므로 팀을 적어도 결재 순서는 바뀌지 않습니다.
                </p>
              </div>
              <div className="col-span-2">
                <label className="ui-label">휴대폰</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="예: 010-1234-5678 (없으면 비워 둠)"
                  maxLength={30}
                  className="ui-input w-full mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  메일 꼬리말 회사 정보의 <b>M</b> 과 견적서·발주서 담당자 연락처에 들어갑니다. 비워 두면 꼬리말에 M 줄이 없습니다.
                </p>
              </div>
            </div>
          </fieldset>

          {/* 권한·이메일·활성 */}
          <fieldset>
            <legend className="text-sm font-semibold mb-2">권한 / 상태</legend>
            <div className="space-y-3 text-sm">
              <div>
                <label className="ui-label">역할 *</label>
                <div className="flex gap-2 mt-1">
                  {(['admin', 'mgmt', 'sales', 'viewer'] as const).map((r) => (
                    <label
                      key={r}
                      className={`flex items-center gap-2 border-2 rounded px-3 py-1.5 cursor-pointer flex-1 text-sm ${
                        role === r
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      } ${isSelf ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={r}
                        checked={role === r}
                        onChange={() => !isSelf && setRole(r)}
                        disabled={isSelf}
                      />
                      <span>
                        <strong>{ROLE_INFO[r].label}</strong>{' '}
                        <span className="text-slate-500 text-xs">
                          {ROLE_INFO[r].sub}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                {isSelf && (
                  <div className="text-xs text-amber-700 mt-1">
                    ⚠ 본인 계정의 역할은 변경할 수 없습니다.
                  </div>
                )}
              </div>

              <div>
                <label className="ui-label">
                  이메일 (비밀번호 재설정 메일용)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="ui-input w-full mt-1"
                />
              </div>

              <label
                className={`flex items-center gap-2 ${
                  isSelf ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => !isSelf && setIsActive(e.target.checked)}
                  disabled={isSelf}
                  className="rounded"
                />
                <span>활성 상태</span>
                {isSelf && (
                  <span className="text-xs text-amber-700">
                    (본인 비활성화 불가)
                  </span>
                )}
              </label>
            </div>
          </fieldset>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700">
              {(error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 -mx-6 -mb-6 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={requestClose}
              className="btn btn-secondary"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? '저장 중…' : isEdit ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </DraggableModal>
  );
}
